/**
 * A aba ativa vive na URL (`?aba=`) — motor ÚNICO.
 *
 * ── Por que um hook, e não a mesma lógica copiada em cada tela
 *
 * O padrão nasceu no `FinanceiroDashboardPage` e foi copiado à mão pro
 * `ConfigCrmPage`. Copiar mais cinco vezes garantiria cinco variações sutis: uma
 * esquece o `replace: true`, outra não trata aba inválida, outra persiste no
 * localStorage "só dessa vez". A regra 12 do projeto é explícita — nunca um
 * segundo motor pra mesma coisa.
 *
 * ── O que ele resolve
 *
 * - **Endereço por aba:** F5 e link compartilhado abrem onde a pessoa estava.
 * - **Nunca `localStorage`** (regra 1): aba é estado de navegação, não
 *   preferência.
 * - **`replace: true`:** trocar de aba não é navegação nova. Sem isso, o Voltar
 *   do navegador desfaz clique de aba em vez de sair da tela — e quem clicou em
 *   seis abas precisa apertar Voltar seis vezes pra escapar.
 * - **Aba inválida AVISA.** Cair na primeira em silêncio foi o que motivou o
 *   teste do catálogo: link velho abria a tela errada e parecia ter funcionado.
 *   Aqui a pessoa é avisada de que o endereço não existe mais.
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useToast } from '@/components/ui';

export interface UseAbaNaUrlOpts<T extends string> {
  /** As abas válidas, na ordem em que aparecem. A primeira é o default. */
  abas: ReadonlyArray<T>;
  /** Nome do parâmetro. Default `aba` — só mude se a tela já usar `aba` pra outra coisa. */
  param?: string;
}

export interface UseAbaNaUrlResultado<T extends string> {
  aba: T;
  setAba: (nova: T) => void;
}

export function useAbaNaUrl<T extends string>({
  abas,
  param = 'aba',
}: UseAbaNaUrlOpts<T>): UseAbaNaUrlResultado<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const valorNaUrl = searchParams.get(param);

  const valida = (v: string | null): v is T => !!v && (abas as ReadonlyArray<string>).includes(v);
  const aba: T = valida(valorNaUrl) ? valorNaUrl : abas[0];

  // Avisa UMA vez por valor inválido. Sem a trava, cada re-render dispararia o
  // toast de novo e a tela viraria uma metralhadora de avisos.
  const jaAvisou = useRef<string | null>(null);
  useEffect(() => {
    if (valorNaUrl && !valida(valorNaUrl) && jaAvisou.current !== valorNaUrl) {
      jaAvisou.current = valorNaUrl;
      toast.show('Essa aba não existe; abrimos a primeira.', { variant: 'info' });
    }
  }, [valorNaUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAba = (nova: T) => {
    setSearchParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set(param, nova);
        return n;
      },
      { replace: true },
    );
  };

  return { aba, setAba };
}
